import { useState, useEffect, useCallback } from 'react';
import Navbar from '../components/common/Navbar';
import { translationsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

const CATEGORY_LABELS = {
  guarani_estructura_guarani: 'Guaraní con Estructura Guaraní',
  guarani_estructura_castellano: 'Guaraní con Estructura Castellano',
  jopara: 'Jopará',
  jehea: 'Jeheá',
};

const CORRECTNESS_LABELS = {
  correct: 'Correcta',
  gn_more: 'Más guaraní',
  es_more: 'Más español',
  incorrect: 'Incorrecta',
};

const DIRECTION_LABELS = {
  'gn-sp': 'GN → SP',
  'sp-gn': 'SP → GN',
};

const STATUS_LABELS = {
  pending: 'Pendiente',
  approved: 'Aprobada',
  returned: 'Devuelta',
  rejected: 'Rechazada',
};

const STATUS_COLORS = {
  pending: 'bg-yellow-100 text-yellow-800',
  approved: 'bg-green-100 text-green-800',
  returned: 'bg-orange-100 text-orange-800',
  rejected: 'bg-red-100 text-red-800',
};

const BAR_COLORS = [
  'bg-blue-500', 'bg-emerald-500', 'bg-violet-500', 'bg-amber-500',
  'bg-rose-500', 'bg-cyan-500', 'bg-fuchsia-500', 'bg-lime-500',
];

const MetricCard = ({ label, value, sub, color = 'text-blue-600' }) => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 flex flex-col gap-1">
    <div className={`text-3xl font-bold ${color}`}>{value}</div>
    <div className="text-sm font-medium text-gray-700">{label}</div>
    {sub && <div className="text-xs text-gray-400">{sub}</div>}
  </div>
);

const DistributionBar = ({ label, count, total, colorClass = 'bg-blue-500' }) => {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <div className="mb-2">
      <div className="flex justify-between text-sm mb-1">
        <span className="text-gray-700">{label}</span>
        <span className="text-gray-500 font-medium">{count} <span className="text-gray-300">({pct}%)</span></span>
      </div>
      <div className="w-full bg-gray-100 rounded-full h-2.5">
        <div className={`${colorClass} h-2.5 rounded-full transition-all duration-500`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

const DailyChart = ({ data }) => {
  const maxCount = Math.max(...data.map(d => d.count), 1);
  const totalDays = data.length;
  // Show only last 30 days, grouped in spans for labels
  const labelIndices = [0, 6, 13, 20, 29].filter(i => i < totalDays);

  return (
    <div>
      <div className="flex items-end gap-0.5 h-28">
        {data.map((d, i) => {
          const h = maxCount > 0 ? Math.max(4, Math.round((d.count / maxCount) * 100)) : 4;
          return (
            <div key={d.date} className="group relative flex-1 flex flex-col items-center justify-end h-full">
              <div
                className="w-full bg-blue-400 rounded-t hover:bg-blue-600 transition-colors cursor-default"
                style={{ height: `${h}%` }}
              />
              {/* Tooltip */}
              <div className="absolute bottom-full mb-1 left-1/2 -translate-x-1/2 z-10 hidden group-hover:block bg-gray-800 text-white text-xs rounded px-2 py-1 whitespace-nowrap shadow-lg">
                {d.date}: {d.count}
              </div>
            </div>
          );
        })}
      </div>
      {/* Date labels under chart */}
      <div className="relative mt-1" style={{ height: 16 }}>
        {labelIndices.map(i => (
          <span
            key={i}
            className="absolute text-xs text-gray-400"
            style={{ left: `${(i / (totalDays - 1)) * 100}%`, transform: 'translateX(-50%)' }}
          >
            {data[i]?.date?.slice(5)}
          </span>
        ))}
      </div>
    </div>
  );
};

const UserTable = ({ users }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b border-gray-100">
          <th className="text-left py-2 text-gray-500 font-medium">Usuario</th>
          <th className="text-right py-2 text-gray-500 font-medium">Validaciones</th>
          <th className="text-right py-2 text-gray-500 font-medium">Sugerencias</th>
        </tr>
      </thead>
      <tbody>
        {users.map((u, idx) => (
          <tr key={u.username} className="border-b border-gray-50 hover:bg-gray-50">
            <td className="py-2 font-medium text-gray-800 flex items-center gap-2">
              <span
                className="inline-block w-2 h-2 rounded-full flex-shrink-0"
                style={{ backgroundColor: `hsl(${(idx * 53) % 360}, 60%, 55%)` }}
              />
              {u.username}
            </td>
            <td className="py-2 text-right text-blue-700 font-semibold">{u.validations}</td>
            <td className="py-2 text-right text-violet-700 font-semibold">{u.suggestions}</td>
          </tr>
        ))}
        {users.length === 0 && (
          <tr><td colSpan={3} className="py-4 text-center text-gray-400">Sin datos</td></tr>
        )}
      </tbody>
    </table>
  </div>
);

const BASE_TABS = [
  { id: 'overview', label: 'Resumen' },
  { id: 'users', label: 'Por Usuario' },
  { id: 'daily', label: 'Actividad Diaria' },
  { id: 'distribution', label: 'Distribuciones' },
];

const TranslationsReportPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  const { user } = useAuth();
  const isAdmin = user?.is_superuser === true;
  const TABS = [...BASE_TABS, ...(isAdmin ? [{ id: 'annotator', label: 'Por Anotador' }] : [])];

  // Por Anotador tab state
  const [annotatorUser, setAnnotatorUser] = useState('');
  const [annotatorData, setAnnotatorData] = useState(null);
  const [annotatorLoading, setAnnotatorLoading] = useState(false);
  const [annotatorError, setAnnotatorError] = useState(null);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const res = await translationsAPI.getReport();
      setData(res.data);
      setError(null);
    } catch (e) {
      setError(`No se pudo cargar el reporte: ${e.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchAnnotatorReport = useCallback(async (username) => {
    if (!username) { setAnnotatorData(null); return; }
    setAnnotatorLoading(true);
    setAnnotatorError(null);
    try {
      const res = await translationsAPI.getReportByAnnotator(username);
      setAnnotatorData(res.data);
    } catch (e) {
      setAnnotatorError(`No se pudo cargar la auditoría: ${e.response?.data?.detail || e.message}`);
    } finally {
      setAnnotatorLoading(false);
    }
  }, []);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Reporte de Traducciones</h1>
            <p className="text-sm text-gray-500 mt-1">Actividad y progreso del corpus de validación</p>
          </div>
          <button
            onClick={fetchReport}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            <svg className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Actualizar
          </button>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4 mb-6 text-sm">{error}</div>
        )}

        {loading && !data && (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
          </div>
        )}

        {data && (
          <>
            {/* Tab navigation */}
            <div className="flex gap-1 bg-white rounded-xl shadow-sm border border-gray-100 p-1 mb-6 overflow-x-auto">
              {TABS.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-1 py-2 px-4 text-sm font-medium rounded-lg transition-colors whitespace-nowrap
                    ${activeTab === tab.id
                      ? 'bg-blue-600 text-white shadow-sm'
                      : 'text-gray-600 hover:bg-gray-50'}`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* OVERVIEW TAB */}
            {activeTab === 'overview' && (
              <div className="space-y-6">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <MetricCard
                    label="Traducciones totales"
                    value={data.corpus.total_translations.toLocaleString()}
                    color="text-gray-800"
                  />
                  <MetricCard
                    label="Completadas (≥3 val.)"
                    value={data.corpus.completed.toLocaleString()}
                    sub={`${data.corpus.completion_pct}% del corpus`}
                    color="text-emerald-600"
                  />
                  <MetricCard
                    label="Validaciones totales"
                    value={data.corpus.total_validations.toLocaleString()}
                    color="text-blue-600"
                  />
                  <MetricCard
                    label="Sugerencias"
                    value={data.corpus.total_suggestions.toLocaleString()}
                    color="text-violet-600"
                  />
                </div>

                {/* Progress bar */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
                  <div className="flex justify-between text-sm mb-2">
                    <span className="font-medium text-gray-700">Progreso del corpus</span>
                    <span className="text-emerald-600 font-bold">{data.corpus.completion_pct}%</span>
                  </div>
                  <div className="w-full bg-gray-100 rounded-full h-4">
                    <div
                      className="bg-emerald-500 h-4 rounded-full transition-all duration-700"
                      style={{ width: `${data.corpus.completion_pct}%` }}
                    />
                  </div>
                  <div className="flex justify-between text-xs text-gray-400 mt-1">
                    <span>{data.corpus.completed} completadas</span>
                    <span>{data.corpus.total_translations} totales</span>
                  </div>
                </div>

                {/* Direction distribution */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
                  <h3 className="font-semibold text-gray-800 mb-4">Validaciones por dirección</h3>
                  {Object.entries(data.direction_distribution).map(([dir, count], i) => (
                    <DistributionBar
                      key={dir}
                      label={DIRECTION_LABELS[dir] || dir}
                      count={count}
                      total={data.corpus.total_validations}
                      colorClass={BAR_COLORS[i % BAR_COLORS.length]}
                    />
                  ))}
                  {Object.keys(data.direction_distribution).length === 0 && (
                    <p className="text-sm text-gray-400">Sin datos</p>
                  )}
                </div>

                {/* Suggestions by status */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
                  <h3 className="font-semibold text-gray-800 mb-4">Estado de sugerencias</h3>
                  <div className="flex flex-wrap gap-3">
                    {Object.entries(data.suggestions_by_status).map(([status, count]) => (
                      <div key={status} className={`px-4 py-2 rounded-lg text-sm font-medium ${STATUS_COLORS[status] || 'bg-gray-100 text-gray-700'}`}>
                        {STATUS_LABELS[status] || status}: <span className="font-bold">{count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* USERS TAB */}
            {activeTab === 'users' && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
                <h3 className="font-semibold text-gray-800 mb-4">Actividad por usuario</h3>
                <UserTable users={data.per_user} />
                {data.per_user.length > 0 && (
                  <div className="mt-6 space-y-2">
                    {data.per_user.map((u, idx) => {
                      const maxVal = data.per_user[0]?.validations || 1;
                      const pct = Math.round((u.validations / maxVal) * 100);
                      return (
                        <div key={u.username} className="flex items-center gap-3">
                          <span className="text-xs text-gray-500 w-28 truncate text-right">{u.username}</span>
                          <div className="flex-1 bg-gray-100 rounded-full h-3">
                            <div
                              className="h-3 rounded-full transition-all duration-500"
                              style={{
                                width: `${pct}%`,
                                backgroundColor: `hsl(${(idx * 53) % 360}, 60%, 55%)`,
                              }}
                            />
                          </div>
                          <span className="text-xs font-bold text-gray-700 w-8 text-right">{u.validations}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* DAILY ACTIVITY TAB */}
            {activeTab === 'daily' && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
                <h3 className="font-semibold text-gray-800 mb-1">Actividad diaria (últimos 30 días)</h3>
                <p className="text-xs text-gray-400 mb-4">Validaciones registradas por día</p>
                {data.daily_activity.length > 0
                  ? <DailyChart data={data.daily_activity} />
                  : <p className="text-sm text-gray-400">Sin datos recientes</p>
                }
                {/* Summary row */}
                <div className="flex gap-4 mt-4 pt-4 border-t border-gray-100 text-sm text-gray-600">
                  <span>Total: <strong>{data.daily_activity.reduce((s, d) => s + d.count, 0)}</strong></span>
                  <span>Días activos: <strong>{data.daily_activity.filter(d => d.count > 0).length}</strong></span>
                  <span>Pico: <strong>{Math.max(...data.daily_activity.map(d => d.count))}</strong></span>
                </div>
              </div>
            )}

            {/* DISTRIBUTIONS TAB */}
            {activeTab === 'distribution' && (
              <div className="grid md:grid-cols-2 gap-6">
                {/* Guaraní category */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
                  <h3 className="font-semibold text-gray-800 mb-4">Clasificación del Guaraní</h3>
                  {Object.keys(data.category_distribution).length > 0 ? (
                    ['guarani_estructura_guarani', 'guarani_estructura_castellano', 'jopara', 'jehea'].map((cat, i) => {
                      const count = data.category_distribution[cat] || 0;
                      const total = Object.values(data.category_distribution).reduce((a, b) => a + b, 0);
                      return (
                        <DistributionBar
                          key={cat}
                          label={CATEGORY_LABELS[cat] || cat}
                          count={count}
                          total={total}
                          colorClass={BAR_COLORS[i % BAR_COLORS.length]}
                        />
                      );
                    })
                  ) : (
                    <p className="text-sm text-gray-400">Sin clasificaciones registradas aún</p>
                  )}
                </div>

                {/* Correctness */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
                  <h3 className="font-semibold text-gray-800 mb-4">Resultado de Validaciones</h3>
                  {Object.keys(data.correctness_distribution).length > 0 ? (
                    ['correct', 'gn_more', 'es_more', 'incorrect'].map((cor, i) => {
                      const count = data.correctness_distribution[cor] || 0;
                      const total = Object.values(data.correctness_distribution).reduce((a, b) => a + b, 0);
                      return (
                        <DistributionBar
                          key={cor}
                          label={CORRECTNESS_LABELS[cor] || cor}
                          count={count}
                          total={total}
                          colorClass={BAR_COLORS[(i + 4) % BAR_COLORS.length]}
                        />
                      );
                    })
                  ) : (
                    <p className="text-sm text-gray-400">Sin validaciones registradas aún</p>
                  )}
                </div>
              </div>
            )}

            {/* POR ANOTADOR TAB (admin) */}
            {activeTab === 'annotator' && isAdmin && (
              <AnnotatorView
                annotators={data.per_user.map(u => u.username)}
                annotatorUser={annotatorUser}
                setAnnotatorUser={setAnnotatorUser}
                data={annotatorData}
                loading={annotatorLoading}
                error={annotatorError}
                onSelect={fetchAnnotatorReport}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

// ─── Por Anotador (audit view, mirrors Prodigy's AuditView) ────────────────

const AnnotatorView = ({ annotators, annotatorUser, setAnnotatorUser, data, loading, error, onSelect }) => (
  <div className="space-y-6">
    <div className="flex flex-wrap items-center gap-3">
      <label className="text-sm text-gray-600 font-medium">Anotador:</label>
      <select
        value={annotatorUser}
        onChange={(e) => { setAnnotatorUser(e.target.value); onSelect(e.target.value); }}
        className="border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-900 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[200px]"
      >
        <option value="">— Seleccionar —</option>
        {annotators.map((a) => <option key={a} value={a}>{a}</option>)}
      </select>
      {data && !loading && annotatorUser && (
        <span className="text-sm text-gray-500">
          Total histórico: <strong className="text-gray-900">{data.total_validations}</strong> validaciones
        </span>
      )}
    </div>
    <p className="text-xs text-gray-400 -mt-4">Histórico completo de validaciones y sugerencias del anotador en traducciones.</p>

    {error && (
      <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4 text-sm">{error}</div>
    )}

    {loading && (
      <div className="bg-white rounded-xl border border-gray-200 p-5 animate-pulse h-32 shadow-sm" />
    )}

    {!loading && !annotatorUser && (
      <div className="bg-white border border-gray-200 rounded-xl p-10 text-center shadow-sm">
        <div className="text-4xl mb-3">🧑‍💻</div>
        <p className="text-gray-500 text-sm">Seleccioná un anotador para ver su histórico completo.</p>
      </div>
    )}

    {data && !loading && annotatorUser && (
      <>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <MetricCard label="Validaciones" value={data.total_validations.toLocaleString()} color="text-blue-600" />
          <MetricCard label="Sugerencias" value={data.total_suggestions.toLocaleString()} color="text-violet-600" />
          <MetricCard label="Primera validación" value={data.first_validation_at ? data.first_validation_at.slice(0, 10) : '—'} color="text-gray-700" />
          <MetricCard label="Última validación" value={data.last_validation_at ? data.last_validation_at.slice(0, 10) : '—'} color="text-gray-700" />
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <h3 className="font-semibold text-gray-800 mb-1">Actividad diaria (últimos 30 días)</h3>
          {data.daily_activity.some(d => d.count > 0)
            ? <DailyChart data={data.daily_activity} />
            : <p className="text-sm text-gray-400">Sin actividad reciente</p>
          }
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
            <h3 className="font-semibold text-gray-800 mb-4">Clasificación del Guaraní</h3>
            {Object.keys(data.category_distribution).length > 0 ? (
              ['guarani_estructura_guarani', 'guarani_estructura_castellano', 'jopara', 'jehea'].map((cat, i) => {
                const count = data.category_distribution[cat] || 0;
                const total = Object.values(data.category_distribution).reduce((a, b) => a + b, 0);
                return (
                  <DistributionBar
                    key={cat}
                    label={CATEGORY_LABELS[cat] || cat}
                    count={count}
                    total={total}
                    colorClass={BAR_COLORS[i % BAR_COLORS.length]}
                  />
                );
              })
            ) : (
              <p className="text-sm text-gray-400">Sin clasificaciones registradas</p>
            )}
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
            <h3 className="font-semibold text-gray-800 mb-4">Resultado de Validaciones</h3>
            {Object.keys(data.correctness_distribution).length > 0 ? (
              ['correct', 'gn_more', 'es_more', 'incorrect'].map((cor, i) => {
                const count = data.correctness_distribution[cor] || 0;
                const total = Object.values(data.correctness_distribution).reduce((a, b) => a + b, 0);
                return (
                  <DistributionBar
                    key={cor}
                    label={CORRECTNESS_LABELS[cor] || cor}
                    count={count}
                    total={total}
                    colorClass={BAR_COLORS[(i + 4) % BAR_COLORS.length]}
                  />
                );
              })
            ) : (
              <p className="text-sm text-gray-400">Sin validaciones registradas</p>
            )}
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <h3 className="font-semibold text-gray-800 mb-4">Validaciones por dirección</h3>
          {Object.entries(data.direction_distribution).map(([dir, count], i) => (
            <DistributionBar
              key={dir}
              label={DIRECTION_LABELS[dir] || dir}
              count={count}
              total={data.total_validations}
              colorClass={BAR_COLORS[i % BAR_COLORS.length]}
            />
          ))}
          {Object.keys(data.direction_distribution).length === 0 && (
            <p className="text-sm text-gray-400">Sin datos</p>
          )}
        </div>
      </>
    )}
  </div>
);

export default TranslationsReportPage;
